import React from "react"
import { StatusBadge } from "@/components/ui/status-badge"
import { PageSection } from "@/components/ui/page-header"
import { cn } from "@/lib/utils"
import type { DemoCallListItem, DemoCallState } from "../types"
import { timestampMs } from "@/lib/datetime"

interface RecentDemoCallsProps {
  calls: DemoCallListItem[]
  activeCallId: string | null
  onSelect: (callId: string) => void
}

const STATE_VARIANT: Record<
  DemoCallState,
  "success" | "info" | "warning" | "destructive" | "muted"
> = {
  COMPLETED: "success",
  IN_PROGRESS: "info",
  PENDING: "info",
  VOICEMAIL: "warning",
  FAILED: "destructive",
  CANCELLED: "muted",
}

const STATE_LABEL: Record<DemoCallState, string> = {
  COMPLETED: "Completed",
  IN_PROGRESS: "On the call",
  PENDING: "Ringing",
  VOICEMAIL: "Voicemail",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
}

function relativeTime(iso: string | null): string {
  if (!iso) return ""
  const then = timestampMs(iso)
  if (Number.isNaN(then)) return ""
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 60) return "just now"
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

export function RecentDemoCalls({
  calls,
  activeCallId,
  onSelect,
}: RecentDemoCallsProps) {
  if (!calls.length) return null

  return (
    <PageSection
      title="Recent demos"
      description="Pick one to reopen its transcript."
    >
      <div className="divide-y rounded-lg border">
        {calls.map((call) => (
          <button
            key={call.call_id}
            type="button"
            onClick={() => onSelect(call.call_id)}
            className={cn(
              "flex w-full items-center gap-4 px-4 py-3 text-left text-sm transition-colors hover:bg-row-hover",
              activeCallId === call.call_id && "bg-row-selected"
            )}
          >
            <StatusBadge variant={STATE_VARIANT[call.status]} dot>
              {STATE_LABEL[call.status] ?? call.status}
            </StatusBadge>
            <span className="font-medium">{call.to_number}</span>
            <span className="text-muted-foreground">
              {call.strategy_type === "CHECK_IN" ? "Check-in" : "Consent"}
            </span>
            <span className="truncate text-muted-foreground">
              {call.persona_name}
            </span>
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">
              {relativeTime(call.created_at)}
            </span>
          </button>
        ))}
      </div>
    </PageSection>
  )
}
